import { notePersistenceAttributes } from '../../utils/note_anchor';

angular.module("LeasePilot").service("DealTermsImportService", [
  "$rootScope",
  "ApiService",
  function($rootScope, ApiService) {
    var service = this;
    var DOCUMENT_BODY_NOTE_TARGET = "document_body";

    // State management
    service.pendingChanges = [];
    service.advisoryChanges = [];
    service.currentChangeIndex = -1;
    service.isImportActive = false;
    service.isProcessing = false; // Processing state for spinner overlay
    service.extractedFields = null;
    service.extractedNotes = [];
    // One sentence from the extraction naming what the notes cover. Empty
    // whenever the server could not vouch for it — see normalize_notes_summary.
    service.extractedNotesSummary = "";
    service.fieldConstraints = [];
    service.rejectedFields = [];
    service.droppedNoteCount = 0;
    service.mutationObserver = null;
    service.observedValues = {}; // Store original values before import
    service.originalLeaseSnapshot = null; // Deep copy of lease before import for rejection

    service.normalizeExtractionResponse = function(raw) {
      var data = raw && raw.data ? raw.data : raw || {};

      if (data.extractedFields || data.extractedNotes) {
        return {
          extractedTerms: data.extractedFields || {},
          extractedNotes: Array.isArray(data.extractedNotes)
            ? data.extractedNotes
            : [],
          extractedNotesSummary: String(data.extractedNotesSummary || "")
        };
      }

      if (data.dealTerms || data.notes) {
        return {
          extractedTerms: data.dealTerms || {},
          extractedNotes: Array.isArray(data.notes) ? data.notes : [],
          extractedNotesSummary: String(data.notesSummary || "")
        };
      }

      return {
        extractedTerms: data || {},
        extractedNotes: [],
        extractedNotesSummary: ""
      };
    };

    /**
     * Scroll to the top of the editor when processing starts
     */
    function scrollToTop() {
      setTimeout(function() {
        var editorSearch = document.querySelector("editor-search");
        if (editorSearch) {
          editorSearch.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 0);
    }

    // Phase two starts from wherever phase one's field review left the panel —
    // often scrolled well down a long list of changes — and its own message is
    // at the top. Exposed rather than copied so both phases scroll the same way
    // to the same element.
    service.scrollToTop = scrollToTop;

    /**
     * Check if an element or any of its ancestors has ng-hide
     * @param {HTMLElement} element - The element to check
     * @returns {Boolean} True if element or any ancestor has ng-hide, false otherwise
     */
    function isElementHidden(element) {
      if (!element) return false;

      // Check if element or any ancestor has ng-hide class
      if (element.closest && element.closest(".ng-hide")) {
        return true;
      }

      return false;
    }

    /**
     * Check if a field path exists in the DOM and is not hidden
     * @param {String} path - The field path (e.g., 'tenantInfo.name', 'rentPeriods[0].totalRent', or 'rentPeriods')
     * @param {Number} arrayIndex - Optional array index if checking a specific array element
     * @returns {Boolean} True if the field exists in the DOM and is visible, false otherwise
     */
    function fieldExistsInDOM(path, arrayIndex) {
      if (!path) return false;

      try {
        // Check if path contains array notation (e.g., 'rentPeriods[0].totalRent')
        var arrayMatch = path.match(/^(.+?)\[(\d+)\](?:\.(.+))?$/);
        var basePath, index, fieldName;

        if (arrayMatch) {
          basePath = arrayMatch[1];
          index = parseInt(arrayMatch[2], 10);
          fieldName = arrayMatch[3];
        } else if (arrayIndex !== undefined) {
          // If arrayIndex is provided, treat path as base array name
          basePath = path;
          index = arrayIndex;
          fieldName = null;
        }

        // If this is an array field
        if (basePath && index !== undefined) {
          var fullBasePath = basePath.startsWith("lease.")
            ? basePath
            : "lease." + basePath;

          // For array fields, the DOM will have: for='lease.rentPeriods[$index].totalRent'
          // We need to query for elements where the 'for' attribute contains:
          // - The base path (e.g., 'lease.rentPeriods')
          // - The field name (e.g., 'totalRent')

          // Build selector to find all editor-control elements with the base path
          var arraySelector = 'editor-control[for*="' + fullBasePath + '"]';

          // If we have a field name, check that the 'for' contains the field name
          if (fieldName) {
            var elements = document.querySelectorAll(arraySelector);
            for (var i = 0; i < elements.length; i++) {
              var forAttr = elements[i].getAttribute("for") || "";
              // Simply check if 'for' contains the field name
              if (forAttr.indexOf(fieldName) !== -1) {
                // Check if element is not hidden
                if (!isElementHidden(elements[i])) {
                  return true;
                }
              }
            }
            return false;
          } else {
            // Just check if any element with this base path exists and is not hidden
            var element = document.querySelector(arraySelector);
            return element && !isElementHidden(element);
          }
        }

        // For non-array fields, check for exact match
        var fullPath = path.startsWith("lease.") ? path : "lease." + path;
        var controlElement = document.querySelector(
          'editor-control[for="' + fullPath + '"]'
        );
        var labelElement = document.querySelector(
          'editor-label[for="' + fullPath + '"]'
        );

        // If not found, try without 'lease.' prefix
        if (!controlElement && !labelElement) {
          controlElement = document.querySelector(
            'editor-control[for="' + path + '"]'
          );
          labelElement = document.querySelector(
            'editor-label[for="' + path + '"]'
          );
        }

        // Check if found elements are not hidden
        if (controlElement && !isElementHidden(controlElement)) {
          return true;
        }
        if (labelElement && !isElementHidden(labelElement)) {
          return true;
        }

        return false;
      } catch (error) {
        console.warn(
          "Error checking if field exists in DOM for path:",
          path,
          error
        );
        return false;
      }
    }

    function isPlainObject(value) {
      return value !== null && typeof value === "object" && !Array.isArray(value);
    }

    /**
     * Deep merge extracted fields into lease object
     * Merges at property level, not replacing entire objects
     * @param {Object} target - The target object (lease) to merge into
     * @param {Object} source - The source object (extracted fields) to merge from
     */
    function deepMerge(target, source) {
      if (!target || !source) return target;

      for (var key in source) {
        if (source.hasOwnProperty(key)) {
          var sourceValue = source[key];
          var targetValue = target[key];

          // Skip null, undefined, or empty string values
          if (
            sourceValue === null ||
            sourceValue === undefined ||
            sourceValue === ""
          ) {
            continue;
          }

          // An extracted array is a partial view of the lease array at the
          // same indices: the model supplies what it read, and each item keeps
          // the editor's own keys (renewalInfo's renewalRentPeriods, which the
          // editor reads unguarded). The extracted length still wins.
          if (Array.isArray(sourceValue) && Array.isArray(targetValue)) {
            sourceValue.forEach(function(item, index) {
              if (isPlainObject(item) && isPlainObject(targetValue[index])) {
                deepMerge(targetValue[index], item);
              } else {
                targetValue[index] = item;
              }
            });
            targetValue.length = sourceValue.length;
          }
          // If source value is an array, replace the entire array
          else if (Array.isArray(sourceValue)) {
            target[key] = sourceValue;
          }
          // If both are objects (and source is not an array), merge recursively
          else if (
            typeof sourceValue === "object" &&
            sourceValue !== null &&
            typeof targetValue === "object" &&
            targetValue !== null &&
            !Array.isArray(targetValue) &&
            !Array.isArray(sourceValue)
          ) {
            // Ensure target has the object
            if (!target[key]) {
              target[key] = {};
            }
            deepMerge(target[key], sourceValue);
          }
          // For primitives or when target doesn't exist, replace the value
          else {
            target[key] = sourceValue;
          }
        }
      }

      return target;
    }

    function normalizeConstraintPath(path) {
      if (!path) return "";

      return String(path)
        .replace(/^lease\./, "")
        .replace(/\[\$parent\.\$index\]/g, "[0]")
        .replace(/\[\$index\]/g, "[0]")
        .replace(/\[\d+\]/g, "[0]")
        .trim();
    }

    function buildFieldConstraintIndex(fieldConstraints) {
      var index = {};

      if (!Array.isArray(fieldConstraints)) return index;

      fieldConstraints.forEach(function(constraint) {
        if (!constraint) return;

        var path = normalizeConstraintPath(constraint.path);
        var category = normalizeConstraintPath(constraint.category);

        if (path) index[path] = constraint;
        if (category) index[category] = constraint;
      });

      return index;
    }

    function findMatchingConstraint(path, constraintIndex) {
      var normalizedPath = normalizeConstraintPath(path);
      return constraintIndex[normalizedPath];
    }

    function hasMatchingConstraint(path, constraintIndex) {
      return !!findMatchingConstraint(path, constraintIndex);
    }

    function normalizeValueForComparison(value) {
      if (value === null || value === undefined) return "";
      return String(value).trim();
    }

    // Read a value from a lease-shaped object using a dotted path with array
    // indices: e.g., "tenantInfo.copyTo[0].name", "rentPeriods[2].rentPerSqFoot".
    // Returns undefined if any segment along the path is missing.
    function getValueAtSnapshotPath(snapshot, path) {
      if (!snapshot || typeof snapshot !== "object" || !path) {
        return undefined;
      }

      var segments = String(path).match(/[^.\[\]]+|\[\d+\]/g);
      if (!segments) return undefined;

      var current = snapshot;
      for (var i = 0; i < segments.length; i++) {
        if (current === null || current === undefined) return undefined;
        var segment = segments[i];
        if (segment.charAt(0) === "[") {
          if (!Array.isArray(current)) return undefined;
          var index = parseInt(segment.substring(1, segment.length - 1), 10);
          current = current[index];
        } else {
          current = current[segment];
        }
      }
      return current;
    }

    // Tolerant value equivalence for pending-change comparison. Handles the
    // assortment of representations Angular uses on the form side (boolean,
    // "number:3", "true"/"false" strings, trailing whitespace, etc.) so a
    // change isn't flagged when the underlying value is materially unchanged.
    function valuesAreEquivalent(left, right) {
      var leftIsEmpty = left === null || left === undefined || left === "";
      var rightIsEmpty = right === null || right === undefined || right === "";
      if (leftIsEmpty && rightIsEmpty) return true;
      if (leftIsEmpty || rightIsEmpty) return false;

      if (left === right) return true;

      if (numericValuesMatch(left, right)) return true;

      var leftBool = parseBooleanValue(left);
      var rightBool = parseBooleanValue(right);
      if (
        (leftBool === true || leftBool === false) &&
        leftBool === rightBool
      ) {
        return true;
      }

      var leftNormalized = normalizeValueForComparison(left).toLowerCase();
      var rightNormalized = normalizeValueForComparison(right).toLowerCase();
      return leftNormalized !== "" && leftNormalized === rightNormalized;
    }

    function angularNumberValue(value) {
      var normalizedValue = normalizeValueForComparison(value);
      var match = normalizedValue.match(/^number:(-?\d+(?:\.\d+)?)$/);
      return match ? match[1] : normalizedValue;
    }

    function numericValuesMatch(left, right) {
      var normalizedLeft = angularNumberValue(left);
      var normalizedRight = angularNumberValue(right);

      if (normalizedLeft === "" || normalizedRight === "") {
        return false;
      }

      var leftNumber = Number(normalizedLeft);
      var rightNumber = Number(normalizedRight);

      if (isNaN(leftNumber) || isNaN(rightNumber)) {
        return false;
      }

      return leftNumber === rightNumber;
    }

    function parseBooleanValue(value) {
      if (value === true || value === false) return value;
      if (value === 1) return true;
      if (value === 0) return false;

      var normalizedValue = normalizeValueForComparison(value).toLowerCase();
      if (normalizedValue === "boolean:true") return true;
      if (normalizedValue === "boolean:false") return false;
      if (["true", "yes", "y", "1"].indexOf(normalizedValue) !== -1) {
        return true;
      }
      if (["false", "no", "n", "0"].indexOf(normalizedValue) !== -1) {
        return false;
      }

      return undefined;
    }

    function valueCandidates(value) {
      var normalizedValue = normalizeValueForComparison(value);
      var candidates = [];

      function addCandidate(candidate) {
        var normalizedCandidate = normalizeValueForComparison(candidate);
        if (
          normalizedCandidate &&
          candidates.indexOf(normalizedCandidate) === -1
        ) {
          candidates.push(normalizedCandidate);
        }
      }

      addCandidate(normalizedValue);

      var parentheticalMatch = normalizedValue.match(/^(.+?)\s*\((.+?)\)$/);
      if (parentheticalMatch) {
        addCandidate(parentheticalMatch[1]);
        addCandidate(parentheticalMatch[2]);
      }

      return candidates;
    }

    function allowedValueMatch(value, constraint) {
      if (
        !constraint ||
        !Array.isArray(constraint.allowedValues) ||
        constraint.allowedValues.length === 0
      ) {
        return { matched: true, value: value };
      }

      var normalizedValue = normalizeValueForComparison(value);
      var normalizedValueCandidates = valueCandidates(value);
      var parsedBooleanValue = parseBooleanValue(value);
      var caseInsensitiveMatch = null;
      var labelMatches = [];

      for (var i = 0; i < constraint.allowedValues.length; i++) {
        var option = constraint.allowedValues[i];
        if (!option) continue;

        var optionValue = normalizeValueForComparison(option.value);
        var optionRawValue = normalizeValueForComparison(option.rawValue);
        var optionLabel = normalizeValueForComparison(option.label);
        var parsedOptionBooleanValue = parseBooleanValue(option.value);
        var parsedOptionRawBooleanValue = parseBooleanValue(option.rawValue);

        if (
          normalizedValue === optionValue ||
          normalizedValue === optionRawValue ||
          normalizedValueCandidates.indexOf(optionValue) !== -1 ||
          normalizedValueCandidates.indexOf(optionRawValue) !== -1 ||
          numericValuesMatch(value, option.value) ||
          numericValuesMatch(value, option.rawValue)
        ) {
          return { matched: true, value: option.value };
        }

        if (
          parsedBooleanValue !== undefined &&
          (parsedBooleanValue === parsedOptionBooleanValue ||
            parsedBooleanValue === parsedOptionRawBooleanValue)
        ) {
          return { matched: true, value: option.value };
        }

        if (
          !caseInsensitiveMatch &&
          normalizedValueCandidates.some(function(candidate) {
            return candidate.toLowerCase() === optionValue.toLowerCase();
          })
        ) {
          caseInsensitiveMatch = option;
        }

        if (
          optionLabel &&
          normalizedValueCandidates.some(function(candidate) {
            return candidate.toLowerCase() === optionLabel.toLowerCase();
          })
        ) {
          labelMatches.push(option);
        }
      }

      if (caseInsensitiveMatch) {
        return { matched: true, value: caseInsensitiveMatch.value };
      }

      if (labelMatches.length === 1) {
        return { matched: true, value: labelMatches[0].value };
      }

      if (labelMatches.length > 1) {
        return {
          matched: false,
          reason: "ambiguous_label_match"
        };
      }

      return { matched: false };
    }

    function booleanConstraintMatch(value, constraint) {
      if (!constraint || constraint.type !== "checkbox") {
        return { applicable: false };
      }

      var parsedValue = parseBooleanValue(value);
      if (parsedValue === undefined) {
        return { applicable: true, matched: false };
      }

      return { applicable: true, matched: true, value: parsedValue };
    }

    function allowedValuesForRejection(constraint) {
      if (!constraint || !Array.isArray(constraint.allowedValues)) return [];

      return constraint.allowedValues.map(function(option) {
        return option && option.value;
      });
    }

    function constraintForRejection(rejection, fieldConstraints) {
      if (!rejection || !Array.isArray(fieldConstraints)) return null;

      var normalizedPath = normalizeConstraintPath(rejection.path);
      for (var i = 0; i < fieldConstraints.length; i++) {
        var constraint = fieldConstraints[i];
        if (!constraint) continue;

        if (
          normalizeConstraintPath(constraint.path) === normalizedPath ||
          normalizeConstraintPath(constraint.category) === normalizedPath
        ) {
          return constraint;
        }
      }

      return null;
    }

    function noteCategoryForPath(path) {
      return normalizeConstraintPath(path);
    }

    function hasExistingNoteForTarget(notes, path, category) {
      if (!Array.isArray(notes)) return false;

      var normalizedPath = normalizeConstraintPath(path);
      var normalizedCategory = normalizeConstraintPath(category);

      return notes.some(function(note) {
        if (!note) return false;

        return (
          (normalizedPath &&
            normalizeConstraintPath(note.path) === normalizedPath) ||
          (normalizedCategory &&
            normalizeConstraintPath(note.category) === normalizedCategory)
        );
      });
    }

    function sanitizeImportNoteText(text) {
      return String(text || "")
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br/>")
        .trim();
    }

    function ensureImportNoteTitle(text, itemCount) {
      var trimmedText = (text || "").trim();
      if (/^Imported Deal Terms Notes?:/i.test(trimmedText)) {
        return trimmedText;
      }

      var title =
        itemCount && itemCount > 1
          ? "Imported Deal Terms Notes:"
          : "Imported Deal Terms Note:";
      return title + " " + trimmedText;
    }

    function noteItemsForRawText(note) {
      var rawText = note && note.raw_text;
      if (rawText && Array.isArray(rawText.items) && rawText.items.length > 0) {
        return rawText.items
          .map(function(item) {
            return String(item || "").trim();
          })
          .filter(function(item) {
            return !!item;
          });
      }

      return [
        String((note && note.text) || "").replace(
          /^Imported Deal Terms Notes?:\s*/i,
          ""
        )
      ].filter(function(item) {
        return !!String(item || "").trim();
      });
    }

    function importGeneratedNote(note) {
      if (!note) return false;

      var rawText = note.raw_text || {};
      return (
        rawText.source === "import_deal_terms" ||
        /^Imported Deal Terms Notes?:/i.test(String(note.text || ""))
      );
    }

    function importNoteTargetKey(note) {
      if (!note) return "";

      // document_body is the absence of a target, not a target. Returning it
      // as a key made every unmatched note look like a note about the same
      // thing, so findExistingImportGeneratedNote merged each new one into the
      // first — undoing the separation normalizeImportGeneratedNotes now does,
      // and re-welding unrelated findings into one paragraph on the way to the
      // lease. An empty key means "no existing note can match this", which is
      // the truth.
      if (
        normalizeConstraintPath(note.category) ===
        DOCUMENT_BODY_NOTE_TARGET
      ) {
        return "";
      }

      return (
        normalizeConstraintPath(note.category) ||
        normalizeConstraintPath(note.path)
      );
    }

    function noteLookupKey(value) {
      return String(value || "")
        .replace(/^lease\./, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    }

    function noteTargetMatches(candidate, targetKey) {
      return !!targetKey && noteLookupKey(candidate) === targetKey;
    }

    function constraintForNoteTarget(path, category) {
      var pathKey = noteLookupKey(path);
      var categoryKey = noteLookupKey(category);

      if (!Array.isArray(service.fieldConstraints)) return null;

      for (var i = 0; i < service.fieldConstraints.length; i++) {
        var constraint = service.fieldConstraints[i];
        if (!constraint) continue;

        if (
          noteTargetMatches(constraint.path, pathKey) ||
          noteTargetMatches(constraint.category, pathKey) ||
          noteTargetMatches(constraint.sectionCategory, pathKey) ||
          noteTargetMatches(constraint.label, pathKey) ||
          noteTargetMatches(constraint.path, categoryKey) ||
          noteTargetMatches(constraint.category, categoryKey) ||
          noteTargetMatches(constraint.sectionCategory, categoryKey) ||
          noteTargetMatches(constraint.label, categoryKey)
        ) {
          return constraint;
        }
      }

      return null;
    }

    function formatImportGeneratedNoteText(items) {
      if (!Array.isArray(items) || items.length === 0) return "";

      if (items.length === 1) {
        return ensureImportNoteTitle(items[0], 1);
      }

      return (
        "Imported Deal Terms Notes:<br/>" +
        items
          .map(function(item, index) {
            return index + 1 + ". " + item;
          })
          .join("<br/>")
      );
    }

    function normalizeImportNoteItemForDedupe(item) {
      return String(item || "")
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    }

    function dedupeImportNoteItems(items) {
      var seenItems = {};
      var dedupedItems = [];

      if (!Array.isArray(items)) return dedupedItems;

      items.forEach(function(item) {
        var dedupeKey = normalizeImportNoteItemForDedupe(item);
        if (!dedupeKey || seenItems[dedupeKey]) return;

        seenItems[dedupeKey] = true;
        dedupedItems.push(item);
      });

      return dedupedItems;
    }

    function buildImportGeneratedNote(note, items) {
      var normalizedItems = (items || [])
        .map(function(item) {
          return sanitizeImportNoteText(item);
        })
        .filter(function(item) {
          return !!item;
        });
      normalizedItems = dedupeImportNoteItems(normalizedItems);

      return {
        path: note.path,
        category: note.category,
        parent_attribute: note.parent_attribute || "Imported Deal Terms",
        text: formatImportGeneratedNoteText(normalizedItems),
        raw_text: {
          source: "import_deal_terms",
          version: "LP-004",
          items: normalizedItems
        }
      };
    }

    function normalizeImportGeneratedNotes(notes) {
      if (!Array.isArray(notes)) return [];

      var groupedNotes = {};
      var targetOrder = [];
      var droppedNoteCount = 0;

      notes.forEach(function(note, noteIndex) {
        if (!note) {
          droppedNoteCount += 1;
          return;
        }

        var text = String(note.text || "").trim();
        if (!text) {
          droppedNoteCount += 1;
          return;
        }

        var path = String(note.path || "").trim();
        var category = String(note.category || "").trim();
        var pathCategory =
          path.indexOf("lease.") === 0 ? noteCategoryForPath(path) : "";
        var categoryFromPath = !category && !!pathCategory;

        if (categoryFromPath) {
          category = pathCategory;
        } else if (category.indexOf("lease.") === 0) {
          category = category.replace(/^lease\./, "");
        }

        var matchingConstraint = constraintForNoteTarget(path, category);
        if (matchingConstraint) {
          path = String(matchingConstraint.path || path).trim();
          category =
            String(matchingConstraint.category || "").trim() ||
            noteCategoryForPath(path);
        } else {
          path = "";
          category = DOCUMENT_BODY_NOTE_TARGET;
        }

        if (!category) {
          droppedNoteCount += 1;
          return;
        }

        var rawItems = noteItemsForRawText(note);

        // Grouping by target is right when the target is a real field: two
        // notes about baseRent are one note about baseRent. It is wrong for
        // document_body, which is not a target at all — it is the bucket for
        // every note that matched no field. Those notes are about unrelated
        // provisions and share only the fact that nothing matched, so grouping
        // them welded four separate findings (Fixed CAM, Workers' Comp,
        // Parking Type, and a list of eight more provisions) into one
        // twenty-line paragraph that no drafter could act on.
        //
        // Keyed by index so each unmatched note stays its own note.
        var targetKey =
          category === DOCUMENT_BODY_NOTE_TARGET
            ? DOCUMENT_BODY_NOTE_TARGET + "#" + noteIndex
            : category || noteCategoryForPath(path);

        if (!groupedNotes[targetKey]) {
          groupedNotes[targetKey] = {
            note: {
              path:
                category === DOCUMENT_BODY_NOTE_TARGET
                  ? ""
                  : path || "lease." + category,
              category: category,
              parent_attribute:
                String(note.parent_attribute || "").trim() ||
                (matchingConstraint &&
                  String(matchingConstraint.parentAttribute || "").trim()) ||
                "Imported Deal Terms"
            },
            items: []
          };
          targetOrder.push(targetKey);
        }

        groupedNotes[targetKey].items = groupedNotes[targetKey].items.concat(
          rawItems
        );
      });

      service.droppedNoteCount = droppedNoteCount;

      return targetOrder.map(function(targetKey) {
        var group = groupedNotes[targetKey];
        return buildImportGeneratedNote(
          {
            path: group.note.path,
            category: group.note.category,
            parent_attribute: group.note.parent_attribute
          },
          group.items
        );
      });
    }

    function mergeImportGeneratedNote(existingNote, incomingNote) {
      var existingItems = noteItemsForRawText(existingNote);
      var incomingItems = noteItemsForRawText(incomingNote);
      var mergedItems = existingItems.concat(incomingItems);

      var mergedNote = buildImportGeneratedNote(
        {
          path: existingNote.path || incomingNote.path,
          category: existingNote.category || incomingNote.category,
          parent_attribute:
            existingNote.parent_attribute ||
            incomingNote.parent_attribute ||
            "Imported Deal Terms"
        },
        mergedItems
      );

      existingNote.path = mergedNote.path;
      existingNote.category = mergedNote.category;
      existingNote.parent_attribute = mergedNote.parent_attribute;
      existingNote.text = mergedNote.text;
      existingNote.raw_text = mergedNote.raw_text;
    }

    function findExistingImportGeneratedNote(notes, incomingNote) {
      var targetKey = importNoteTargetKey(incomingNote);
      if (!targetKey) return null;

      for (var i = 0; i < notes.length; i++) {
        var note = notes[i];
        if (
          importGeneratedNote(note) &&
          importNoteTargetKey(note) === targetKey
        ) {
          return note;
        }
      }

      return null;
    }

    function appendImportGeneratedNotesToLease(lease, notes) {
      if (!lease || !Array.isArray(notes) || notes.length === 0) return;

      if (!Array.isArray(lease.notes)) {
        lease.notes = [];
      }
      lease.notesAttributes = [];

      notes.forEach(function(note) {
        var existingImportNote = findExistingImportGeneratedNote(
          lease.notes,
          note
        );

        if (existingImportNote) {
          mergeImportGeneratedNote(existingImportNote, note);
          if (existingImportNote.id) {
            lease.notesAttributes.push(
              Object.assign(
                { id: existingImportNote.id },
                notePersistenceAttributes(existingImportNote)
              )
            );
          } else {
            lease.notesAttributes.push(notePersistenceAttributes(existingImportNote));
          }
          return;
        }

        lease.notes.push(note);
        lease.notesAttributes.push(notePersistenceAttributes(note));
      });

      $rootScope.$broadcast("dealTermsImport:notesUpdated");
    }

    function importGeneratedNoteTargetCount(notes) {
      var targets = {};

      if (!Array.isArray(notes)) return 0;

      notes.forEach(function(note) {
        var targetKey = importNoteTargetKey(note);
        if (targetKey) {
          targets[targetKey] = true;
        }
      });

      return Object.keys(targets).length;
    }

    function importOutcomeMetadata() {
      var pendingChanges = service.pendingChanges || [];
      var userRejectedChanges = pendingChanges.filter(function(change) {
        return change && change.decision === "rejected";
      }).length;

      return {
        applied_field_count: pendingChanges.length - userRejectedChanges,
        rejected_field_count:
          userRejectedChanges + (service.rejectedFields || []).length,
        generated_note_count: (service.extractedNotes || []).length,
        fields_with_generated_notes_count: importGeneratedNoteTargetCount(
          service.extractedNotes
        ),
        validation_rejection_count: (service.rejectedFields || []).length,
        dropped_note_count: service.droppedNoteCount || 0
      };
    }

    function trackImportOutcome(outcome) {
      if (
        !ApiService ||
        typeof ApiService.trackDealTermsImportOutcome !== "function"
      ) {
        return;
      }

      ApiService.trackDealTermsImportOutcome({
        outcome: outcome,
        metadata: importOutcomeMetadata()
      }).catch(function(error) {
        if (window.isDebug) {
          console.warn("Failed to track deal terms import outcome:", error);
        }
      });
    }

    function rejectionValueForNote(value) {
      if (value === null || value === undefined) return "";
      if (typeof value === "string") return value.trim();

      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    }

    function buildRejectionNoteText(rejection, constraint) {
      var fieldLabel =
        (constraint && (constraint.label || constraint.category || constraint.path)) ||
        rejection.path;
      var sourceValue = rejectionValueForNote(rejection.value);

      if (rejection.reason === "not_allowed_value") {
        var allowedValues = Array.isArray(rejection.allowed)
          ? rejection.allowed.filter(function(value) {
              return value !== null && value !== undefined && value !== "";
            })
          : [];
        var allowedText =
          allowedValues.length > 0
            ? " Supported values are: " + allowedValues.join(", ") + "."
            : "";

        return (
          "Imported Deal Terms Note: Source document specified " +
          "'" +
          sourceValue +
          "' for " +
          fieldLabel +
          ", but that value is not supported by this document setup." +
          allowedText +
          " Drafter should review."
        );
      }

      if (rejection.reason === "ambiguous_label_match") {
        return (
          "Imported Deal Terms Note: Source document specified " +
          "'" +
          sourceValue +
          "' for " +
          fieldLabel +
          ", but it ambiguously matches multiple available options. Drafter should review."
        );
      }

      return (
        "Imported Deal Terms Note: Source document included " +
        "'" +
        sourceValue +
        "' for " +
        fieldLabel +
        ", but this field is not available in the current document setup. Drafter should review."
      );
    }

    function materialRejection(rejection) {
      if (!rejection || rejection.reason === "not_in_setup") return false;

      return !!rejectionValueForNote(rejection.value);
    }

    function appendNotesForRejections(extractedNotes, rejections, fieldConstraints) {
      var notes = Array.isArray(extractedNotes) ? extractedNotes.slice() : [];

      if (!Array.isArray(rejections) || rejections.length === 0) {
        return notes;
      }

      rejections.forEach(function(rejection) {
        if (!materialRejection(rejection)) return;

        var constraint = constraintForRejection(rejection, fieldConstraints);
        var category =
          (constraint && constraint.category) || noteCategoryForPath(rejection.path);
        var path =
          (constraint && constraint.path) ||
          (rejection.path && "lease." + noteCategoryForPath(rejection.path));

        if (hasExistingNoteForTarget(notes, path, category)) return;

        notes.push({
          path: path,
          category: category,
          parent_attribute:
            (constraint && constraint.parentAttribute) || "Imported Deal Terms",
          raw_text: {
            source: "import_deal_terms",
            version: "LP-004",
            items: [buildRejectionNoteText(rejection, constraint).replace(/^Imported Deal Terms Notes?:\s*/i, "")]
          },
          text: buildRejectionNoteText(rejection, constraint)
        });
      });

      return notes;
    }

    function filterExtractedFieldsByConstraints(extractedFields, fieldConstraints) {
      if (!extractedFields || typeof extractedFields !== "object") {
        return { fields: extractedFields || {}, rejections: [] };
      }

      if (!Array.isArray(fieldConstraints) || fieldConstraints.length === 0) {
        return { fields: extractedFields, rejections: [] };
      }

      var constraintIndex = buildFieldConstraintIndex(fieldConstraints);
      var rejections = [];

      function filterValue(value, path) {
        if (value === null || value === undefined || value === "") {
          return { keep: false };
        }

        if (Array.isArray(value)) {
          var filteredArray = [];

          value.forEach(function(item, index) {
            var itemPath = path + "[" + index + "]";
            var filteredItem = filterValue(item, itemPath);
            if (filteredItem.keep) {
              filteredArray.push(filteredItem.value);
            }
          });

          if (filteredArray.length > 0) {
            return { keep: true, value: filteredArray };
          }

          if (hasMatchingConstraint(path, constraintIndex)) {
            return { keep: true, value: value };
          }

          rejections.push({
            path: path,
            reason: "not_in_setup",
            value: value
          });
          return { keep: false };
        }

        if (typeof value === "object") {
          var filteredObject = {};

          Object.keys(value).forEach(function(key) {
            var childPath = path ? path + "." + key : key;
            var filteredChild = filterValue(value[key], childPath);

            if (filteredChild.keep) {
              filteredObject[key] = filteredChild.value;
            }
          });

          if (Object.keys(filteredObject).length > 0) {
            return { keep: true, value: filteredObject };
          }

          if (path && hasMatchingConstraint(path, constraintIndex)) {
            return { keep: true, value: value };
          }

          // Array-marker fallback: if this object is an indexed item of an
          // array whose parent path has an arrayMarker constraint (emitted
          // by FieldConstraintsService Phase 2 for ng-repeat directives the
          // active form renders), accept the original item value. This lets
          // sub-fields of currently-empty arrays — which have no live
          // editor-control to seed a leaf-level constraint — flow through.
          var arrayItemMatch = path && path.match(/^(.+?)\[\d+\]$/);
          if (arrayItemMatch) {
            var arrayParentPath = arrayItemMatch[1];
            var arrayParentConstraint = findMatchingConstraint(
              arrayParentPath,
              constraintIndex
            );
            if (arrayParentConstraint && arrayParentConstraint.arrayMarker) {
              return { keep: true, value: value };
            }
          }

          return { keep: false };
        }

        var matchingConstraint = findMatchingConstraint(path, constraintIndex);
        if (matchingConstraint) {
          var booleanMatch = booleanConstraintMatch(value, matchingConstraint);
          if (booleanMatch.applicable) {
            if (booleanMatch.matched) {
              return { keep: true, value: booleanMatch.value };
            }

            rejections.push({
              path: path,
              reason: "not_allowed_value",
              value: value,
              allowed: [true, false]
            });
            return { keep: false };
          }

          if (
            !Array.isArray(matchingConstraint.allowedValues) ||
            matchingConstraint.allowedValues.length === 0
          ) {
            return { keep: true, value: value };
          }

          var allowedMatch = allowedValueMatch(value, matchingConstraint);
          if (allowedMatch.matched) {
            return { keep: true, value: allowedMatch.value };
          }

          rejections.push({
            path: path,
            reason: allowedMatch.reason || "not_allowed_value",
            value: value,
            allowed: allowedValuesForRejection(matchingConstraint)
          });
          return { keep: false };
        }

        rejections.push({
          path: path,
          reason: "not_in_setup",
          value: value
        });
        return { keep: false };
      }

      return {
        fields: filterValue(extractedFields, "").value || {},
        rejections: rejections
      };
    }

    /**
     * Capture current values from all editor-control elements in the DOM
     * @returns {Object} Map of field paths to their current values
     */
    function captureCurrentValues() {
      var values = {};
      var controls = document.querySelectorAll("editor-control[for]");

      controls.forEach(function(control) {
        var forAttr = control.getAttribute("for");
        if (!forAttr || !forAttr.startsWith("lease.")) return;

        // Get the field path without 'lease.' prefix
        var fieldPath = forAttr.replace(/^lease\./, "");

        // Handle array fields - extract index from attribute
        var indexAttr = control.getAttribute("index");
        if (indexAttr !== null) {
          // Replace $index in the path with actual index
          fieldPath = fieldPath.replace(/\$index/g, indexAttr);
          // Also handle $parent.$index if needed
          var parentIndexAttr = control.getAttribute("parent-index");
          if (parentIndexAttr !== null) {
            fieldPath = fieldPath.replace(
              /\$parent\.\$index/g,
              parentIndexAttr
            );
          }
        }

        // Get the actual input value
        var input = control.querySelector(
          'input[type="text"], input[type="number"], input[type="date"], textarea'
        );
        var select = control.querySelector("select");
        var checkbox = control.querySelector("md-checkbox");
        var radioGroup = control.querySelector("md-radio-group");

        var value = null;
        if (input) {
          value = input.value;
          // Convert to number if it's a number input
          if (input.type === "number" && value !== "") {
            value = parseFloat(value);
          }
        } else if (select) {
          value = select.value;
          // Filter out placeholder/invalid values from dropdowns
          if (
            value === "?" ||
            value === "? undefined:undefined ?" ||
            value.indexOf("?") !== -1
          ) {
            value = undefined;
          }
        } else if (checkbox) {
          // Get Angular scope for checkbox
          var checkboxScope = angular.element(checkbox).scope();
          if (checkboxScope && checkboxScope.$parent) {
            // Try to get the ng-model value
            var ngModel = angular.element(checkbox).controller("ngModel");
            if (ngModel) {
              value = ngModel.$viewValue;
            }
          }
        } else if (radioGroup) {
          // Get Angular scope for radio group
          var radioScope = angular.element(radioGroup).scope();
          if (radioScope && radioScope.$parent) {
            var ngModel = angular.element(radioGroup).controller("ngModel");
            if (ngModel) {
              value = ngModel.$viewValue;
            }
          }
        }

        // Filter out placeholder/invalid string values
        if (typeof value === "string") {
          var trimmedValue = value.trim();
          if (
            trimmedValue === "?" ||
            trimmedValue === "? undefined:undefined ?" ||
            trimmedValue.indexOf("?") !== -1
          ) {
            value = undefined;
          }
        }

        if (value !== null && value !== undefined) {
          values[fieldPath] = value;
        }
      });

      return values;
    }

    /**
     * Compare captured values before and after import to identify changes
     * @param {Object} beforeValues - Values before import
     * @param {Object} afterValues - Values after import
     * @returns {Array} Array of change objects
     */
    function identifyChangesFromDOM(beforeValues, afterValues) {
      var changes = [];
      var allPaths = new Set();

      // Collect all paths
      Object.keys(beforeValues).forEach(function(path) {
        allPaths.add(path);
      });
      Object.keys(afterValues).forEach(function(path) {
        allPaths.add(path);
      });

      allPaths.forEach(function(path) {
        var before = beforeValues[path];
        var after = afterValues[path];

        // Only create change if values are different
        if (before !== after) {
          // Get category from DOM
          // var category = getCategoryFromPath(path);

          changes.push({
            path: path,
            field: path.split(".").pop() || path,
            // category: category,
            before: before,
            after: after,
            extracted: after,
            current: before
          });
        }
      });

      return changes;
    }

    service.updateCalculatedFields = function() {
      const appScope = $rootScope.findAppScope();
      appScope.calculatedFields.init();
    };

    service.saveChangesToLease = function() {
      const appScope = $rootScope.findAppScope();
      const hasQueuedNotes =
        appScope.lease &&
        Array.isArray(appScope.lease.notesAttributes) &&
        appScope.lease.notesAttributes.length > 0;

      let updatePromise = appScope.update();

      if (hasQueuedNotes && typeof appScope.update.flush === "function") {
        updatePromise = appScope.update.flush();
      }

      // Clear the nested-attributes queue after the save has been forced to start
      // so later autosaves don't recreate the same notes.
      if (hasQueuedNotes) {
        appScope.lease.notesAttributes = [];
      }

      return updatePromise;
    };

    /**
     * Helper function to normalize field paths for sorting
     * @param {String} path - The field path
     * @returns {String} Normalized path
     */
    function normalizeFieldPathForSorting(path) {
      if (!path) return path;
      // Replace $index and $parent.$index patterns with [0]
      path = path.replace(/\[\$parent\.\$index\]/g, "[0]");
      path = path.replace(/\[\$index\]/g, "[0]");
      // Replace any numeric index (e.g., [0], [1], [2]) with [0] for sorting
      path = path.replace(/\[\d+\]/g, "[0]");
      return path;
    }

    /**
     * Apply values to lease, wait for Angular to digest, identify changes, sort them,
     * trigger events, filter, and save. This is the core logic used by both
     * initializeImport and rejectAllChanges.
     * @param {Object} currentLease - The current lease object
     * @param {Object} valuesToMerge - Values to merge into the lease (extractedFields or originalLeaseSnapshot)
     * @param {Object} beforeValues - Values captured before merging (for change detection)
     * @param {Function} resolve - Promise resolve function
     * @param {Function} reject - Promise reject function
     * @param {Boolean} shouldReplace - If true, completely replace the lease instead of merging
     */
    function applyValuesAndProcessChanges(
      currentLease,
      valuesToMerge,
      beforeValues,
      resolve,
      reject,
      shouldReplace
    ) {
      try {
        // Step 2: Apply values to the lease
        if (shouldReplace) {
          // Completely replace the lease: clear all properties and copy from snapshot
          // We can't just do currentLease = valuesToMerge because that would break Angular's binding
          // Instead, we modify the object in place by clearing and then deep copying
          Object.keys(currentLease).forEach(function(key) {
            delete currentLease[key];
          });
          // Deep copy all properties from snapshot to preserve nested objects/arrays
          var snapshotCopy = JSON.parse(JSON.stringify(valuesToMerge));
          Object.keys(snapshotCopy).forEach(function(key) {
            currentLease[key] = snapshotCopy[key];
          });
        } else {
          // Deep merge values into the lease (for initial import)
          // We merge everything - let Angular render what it can
          deepMerge(currentLease, valuesToMerge);
        }

        // Derive the section flags for everything just written, exactly as
        // acceptChangeForField does for a single field. Both write paths need
        // it and only one had it: accepting a field individually normalised,
        // but applying the whole import — which is what continuing to phase two
        // does — did not. An imported value then sat on the lease with its
        // section flag still off, so the section stayed hidden and dropped out
        // of the download, with nothing on screen to say so.
        //
        // Called without a changedPath, the whole-lease form used at load
        // (lease_editor_controller.js:13183), because every merged leaf is new
        // here rather than one known field.
        try {
          var flagScope = $rootScope.findAppScope && $rootScope.findAppScope();
          if (flagScope && flagScope.normalizeDependentFlags) {
            flagScope.normalizeDependentFlags(currentLease);
          }
        } catch (e) {
          // A flag that cannot be derived must not take the import down with
          // it: the values are already applied by this point.
          console.warn("Failed to normalize dependent flags after apply:", e);
        }

        // Step 3: Wait for Angular to digest and update the DOM
        // Then capture new values and identify changes
        setTimeout(function() {
          var newValues = captureCurrentValues();
          service.pendingChanges = identifyChangesFromDOM(
            beforeValues,
            newValues
          );

          // Step 4a: Inject pending changes for any leaf in valuesToMerge that
          // has a matching field constraint but didn't appear in the DOM diff.
          // This covers fields whose editor controls weren't in the DOM at
          // capture time — most notably new array entries populated under an
          // ng-repeat whose array was previously empty. Without this, the
          // values are merged into the lease but never surface to the user as
          // accept/reject changes.
          try {
            var injectionConstraintIndex = buildFieldConstraintIndex(
              service.fieldConstraints
            );
            var existingChangePaths = new Set();
            service.pendingChanges.forEach(function(change) {
              if (change && change.path) {
                existingChangePaths.add(change.path);
              }
            });

            function walkAndInject(value, path) {
              if (value === null || value === undefined || value === "") {
                return;
              }

              if (Array.isArray(value)) {
                value.forEach(function(item, index) {
                  walkAndInject(item, path + "[" + index + "]");
                });
                return;
              }

              if (typeof value === "object") {
                Object.keys(value).forEach(function(key) {
                  var childPath = path ? path + "." + key : key;
                  walkAndInject(value[key], childPath);
                });
                return;
              }

              if (!path || existingChangePaths.has(path)) return;
              if (!hasMatchingConstraint(path, injectionConstraintIndex)) {
                return;
              }

              service.pendingChanges.push({
                path: path,
                field: path.split(".").pop() || path,
                before: undefined,
                after: value,
                extracted: value,
                current: undefined
              });
              existingChangePaths.add(path);
            }

            walkAndInject(valuesToMerge, "");
          } catch (injectionError) {
            console.warn(
              "Failed to inject pending changes from extractedFields:",
              injectionError
            );
          }

          // Step 4b: Sort changes by their order in the DOM so navigation/summary
          // follow the visual top-to-bottom layout.
          // We leverage the fact that each editor control has a `for="lease.X"` attribute
          // and the editor_control directive maps that to a `data-field-path="X"`.
          try {
            var controls = Array.prototype.slice.call(
              document.querySelectorAll('editor-control[for^="lease."]')
            );

            if (controls.length > 0) {
              var indexByFieldPath = {};

              controls.forEach(function(ctrl, idx) {
                var forAttr = ctrl.getAttribute("for");
                if (!forAttr) return;
                // Normalize to the same format used in pendingChanges.path
                var fieldPath = forAttr.replace(/^lease\./, "");
                // Normalize the field path for sorting (replace indices with [0])
                var normalizedPath = normalizeFieldPathForSorting(fieldPath);
                // Keep the first occurrence (lowest DOM index) for each normalized path
                if (indexByFieldPath[normalizedPath] === undefined) {
                  indexByFieldPath[normalizedPath] = idx;
                }
              });

              service.pendingChanges.sort(function(a, b) {
                if (!a || !a.path) return 1;
                if (!b || !b.path) return -1;

                // Normalize both paths for comparison
                var normalizedPathA = normalizeFieldPathForSorting(a.path);
                var normalizedPathB = normalizeFieldPathForSorting(b.path);

                var ia = indexByFieldPath[normalizedPathA];
                var ib = indexByFieldPath[normalizedPathB];

                if (ia === undefined && ib === undefined) return 0;
                if (ia === undefined) return 1;
                if (ib === undefined) return -1;

                return ia - ib;
              });
            }
          } catch (e) {
            // If DOM is unavailable for any reason, fall back to unsorted changes
            console.warn("Unable to sort deal terms changes by DOM order:", e);
          }

          setTimeout(() => {
            // Step 5: Trigger change events on all affected controls
            // This ensures ng-change handlers, update(), and updateCurrent() are called
            // Must happen after DOM is updated and changes are identified
            service.triggerChangeEvents();
            service.updateCalculatedFields();

            setTimeout(() => {
              // Step 5b: Filter changes to only include fields that the form
              // actually supports. A change is kept when EITHER:
              //   (a) its path matches a fieldConstraint sent at extraction
              //       time — this covers nested fields whose visibility is
              //       gated by a parent toggle/dropdown that the extractor
              //       activated, even if Angular has not yet re-rendered the
              //       child control by this setTimeout; or
              //   (b) its control exists and is currently visible in the DOM
              //       — fallback for paths the constraint payload missed.
              // Previously this filter required visibility only, which caused
              // nested values (e.g., tiAllowance.amount, guarantorInfo.*) to
              // be silently dropped whenever their parent toggle was off at
              // import time even if the extractor correctly emitted both the
              // toggle and the child.
              var pendingChangeConstraintIndex = buildFieldConstraintIndex(
                service.fieldConstraints
              );
              service.pendingChanges = service.pendingChanges.filter(function(
                change
              ) {
                if (!change || !change.path) return false;
                if (
                  hasMatchingConstraint(change.path, pendingChangeConstraintIndex)
                ) {
                  return true;
                }
                return fieldExistsInDOM(change.path);
              });

              // Step 5c: drop spurious pending changes by comparing each
              // change against the original lease snapshot taken before any
              // merge. If the extracted value already equals what the lease
              // had, this isn't a real change — keeping it would create a
              // misleading pending-change card. The classic symptom is a
              // radio control where the snapshot's value matched the
              // extracted value but the control was previously hidden (so
              // its DOM "before" was undefined), causing identifyChangesFromDOM
              // or Step 4a to flag a non-existent change and visually
              // highlight every option of the radio at once.
              if (
                service.originalLeaseSnapshot &&
                Array.isArray(service.pendingChanges) &&
                service.pendingChanges.length > 0
              ) {
                service.pendingChanges = service.pendingChanges.filter(function(
                  change
                ) {
                  if (!change || !change.path) return true;
                  var snapshotValue = getValueAtSnapshotPath(
                    service.originalLeaseSnapshot,
                    change.path
                  );
                  var extractedValue =
                    change.extracted !== undefined
                      ? change.extracted
                      : change.after;
                  return !valuesAreEquivalent(snapshotValue, extractedValue);
                });
              }

              // Set first change as active if any exist
              if (service.pendingChanges.length > 0) {
                service.currentChangeIndex = 0;
                service.navigateToField(service.pendingChanges[0]);
              }

              setTimeout(() => {
                // Step 6: Save the changes to the lease
                service.saveChangesToLease();

                // Add class to expand sidebar
                if (document.body) {
                  document.body.classList.add("deal-terms-import-expanded");
                }

                // Broadcast that import has started
                $rootScope.$broadcast("dealTermsImport:started", {
                  changes: service.pendingChanges
                });

                // Stop processing and resolve the promise when initialization is complete
                service.isProcessing = false;
                $rootScope.$broadcast("dealTermsImport:processingFinished");
                resolve();
              }, 500);
            }, 1000);
          }, 1000);
        }, 100); // Give Angular time to digest and update DOM
      } catch (error) {
        service.isProcessing = false;
        $rootScope.$broadcast("dealTermsImport:processingFinished");
        reject(error);
      }
    }

    /**
     * Initialize import with extracted fields
     * Uses DOM observation to detect actual changes after applying updates
     * @param {Object} extractedFields - The extracted fields from AI
     * @param {Object} currentLease - The current lease object
     * @param {Object} options - Optional import metadata such as fieldConstraints/extractedNotes
     * @returns {Promise} Promise that resolves when import initialization is complete
     */
    service.initializeImport = function(extractedFields, currentLease, options) {
      return new Promise(function(resolve, reject) {
        try {
          options = options || {};
          service.isProcessing = true;
          scrollToTop();
          $rootScope.$broadcast("dealTermsImport:processingStarted");

          service.extractedFields = extractedFields;
          service.droppedNoteCount = 0;
          service.extractedNotes = Array.isArray(options.extractedNotes)
            ? options.extractedNotes.slice()
            : [];
          service.extractedNotesSummary = String(
            options.extractedNotesSummary || ""
          );
          service.fieldConstraints = Array.isArray(options.fieldConstraints)
            ? options.fieldConstraints.slice()
            : [];
          var validationResult = filterExtractedFieldsByConstraints(
            extractedFields,
            service.fieldConstraints
          );
          service.rejectedFields = validationResult.rejections;
          service.extractedFields = validationResult.fields;
          service.extractedNotes = appendNotesForRejections(
            service.extractedNotes,
            service.rejectedFields,
            service.fieldConstraints
          );
          service.extractedNotes = normalizeImportGeneratedNotes(
            service.extractedNotes
          );
          appendImportGeneratedNotesToLease(currentLease, service.extractedNotes);
          if (window.isDebug && service.rejectedFields.length > 0) {
            console.log(
              "DealTermsImportService rejected extracted fields outside setup:",
              service.rejectedFields.length
            );
          }
          service.pendingChanges = [];
          service.currentChangeIndex = -1;
          service.isImportActive = true;

          // Step 1: Create a deep copy of the original lease before making any changes
          // This will be used to restore the lease when rejecting all changes
          service.originalLeaseSnapshot = JSON.parse(
            JSON.stringify(currentLease)
          );

          // Step 1b: Capture current values from DOM before making any changes
          service.observedValues = captureCurrentValues();

          // Use the shared function to apply values and process changes
          // Pass false to use merge for initial import
          applyValuesAndProcessChanges(
            currentLease,
            service.extractedFields,
            service.observedValues,
            resolve,
            reject,
            false
          );
        } catch (error) {
          service.isProcessing = false;
          $rootScope.$broadcast("dealTermsImport:processingFinished");
          reject(error);
        }
      });
    };

    /**
     * Get category from DOM by finding the control and its parent card
     * @param {String} path - The field path (e.g., 'tenantInfo.name')
     * @returns {String} The category name from the card title, or 'Other' if not found
     */
    function getCategoryFromPath(path) {
      if (!path) return "Other";

      try {
        // Build the full path for the 'for' attribute (e.g., 'lease.tenantInfo.name')
        var fullPath = path.startsWith("lease.") ? path : "lease." + path;

        // Find the editor-control element with matching 'for' attribute
        var controlElement = document.querySelector(
          'editor-control[for="' + fullPath + '"]'
        );

        if (!controlElement) {
          // Try without 'lease.' prefix
          controlElement = document.querySelector(
            'editor-control[for="' + path + '"]'
          );
        }

        if (!controlElement) {
          return "Other";
        }

        // Traverse up to find the parent collapse-card
        var cardElement = controlElement.closest(".collapse-card");

        if (!cardElement) {
          return "Other";
        }

        // Find the card title
        var titleElement = cardElement.querySelector(".collapse-card__title");

        if (!titleElement) {
          return "Other";
        }

        // Get the title text - handle both input (editing mode) and span (display mode)
        var titleText = "";
        var inputElement = titleElement.querySelector("input.label");
        var spanElement = titleElement.querySelector("span:not(.term-number)");

        if (inputElement && inputElement.value) {
          titleText = inputElement.value.trim();
        } else if (spanElement) {
          titleText = spanElement.textContent.trim();
        } else {
          // Fallback: get all text content and clean it up
          titleText = titleElement.textContent.trim();
          // Remove term number if present (e.g., "Term 1/3")
          titleText = titleText.replace(/\d+\/\d+$/, "").trim();
        }

        return titleText || "Other";
      } catch (error) {
        console.warn("Error getting category from DOM for path:", path, error);
        return "Other";
      }
    }

    // Helper: normalize dropdown values from "number:X" format to actual numbers
    function normalizeDropdownValue(value) {
      if (typeof value === "string") {
        // Check if value is in "number:X" format (from Angular ng-options)
        var numberMatch = value.match(/^number:(\d+(?:\.\d+)?)$/);
        if (numberMatch) {
          // Extract and convert to number
          var numValue = parseFloat(numberMatch[1]);
          return isNaN(numValue) ? value : numValue;
        }
      }
      return value;
    }

    // Helper: set a value on an object by a dotted/array path
    function setValueByPath(obj, path, value) {
      if (!obj || !path) return;

      // Normalize dropdown values (e.g., "number:3" -> 3)
      value = normalizeDropdownValue(value);

      var pathParts = path.split(".");
      var target = obj;

      // Navigate to the target object
      for (var i = 0; i < pathParts.length - 1; i++) {
        var part = pathParts[i];

        // Handle array indices like terms[0]
        var arrayMatch = part.match(/^(\w+)\[(\d+)\]$/);
        if (arrayMatch) {
          var arrayName = arrayMatch[1];
          var arrayIndex = parseInt(arrayMatch[2], 10);
          if (!target[arrayName]) {
            target[arrayName] = [];
          }
          if (!target[arrayName][arrayIndex]) {
            target[arrayName][arrayIndex] = {};
          }
          target = target[arrayName][arrayIndex];
        } else {
          if (!target[part]) {
            target[part] = {};
          }
          target = target[part];
        }
      }

      // Set the value
      var fieldName = pathParts[pathParts.length - 1];

      // Handle array indices in field name
      var fieldArrayMatch = fieldName.match(/^(\w+)\[(\d+)\]$/);
      if (fieldArrayMatch) {
        var fieldArrayName = fieldArrayMatch[1];
        var fieldArrayIndex = parseInt(fieldArrayMatch[2], 10);
        if (!target[fieldArrayName]) {
          target[fieldArrayName] = [];
        }
        target[fieldArrayName][fieldArrayIndex] = value;
      } else {
        target[fieldName] = value;
      }
    }

    service.getLabelPath = function(change) {
      if (!change || !change.path) return "";
      // If path already starts with 'lease.', return as is
      if (change.path.startsWith("lease.")) {
        return change.path;
      }
      // Otherwise, prepend 'lease.'
      return "lease." + change.path;
    };

    // Debounced navigation state
    var navigateTimeoutId = null;
    var lastNavigatedPath = null;

    service.navigateToField = function(change) {
      if (!change || !change.path) return;

      // Get the full path (e.g., 'lease.tenantInfo.name')
      var fullPath = service.getLabelPath(change);

      // Avoid redundant navigations to the same field
      if (fullPath === lastNavigatedPath) {
        return;
      }

      if (!window.leftSideEditorSearch) {
        return;
      }

      // Debounce DOM-heavy search calls
      if (navigateTimeoutId) {
        clearTimeout(navigateTimeoutId);
        navigateTimeoutId = null;
      }

      navigateTimeoutId = setTimeout(function() {
        // In case the editor was torn down in the meantime
        if (window.leftSideEditorSearch) {
          window.leftSideEditorSearch.searchByVariableName(fullPath, 0);
          lastNavigatedPath = fullPath;
        }
      });
    };

    /**
     * Navigate to next change
     */
    service.nextChange = function() {
      if (service.currentChangeIndex < service.pendingChanges.length - 1) {
        service.currentChangeIndex++;
        var change = service.pendingChanges[service.currentChangeIndex];
        service.navigateToField(change);
        $rootScope.$broadcast("dealTermsImport:changeNavigated", {
          index: service.currentChangeIndex,
          change: change
        });
      }
    };

    /**
     * Navigate to previous change
     */
    service.previousChange = function() {
      if (service.currentChangeIndex > 0) {
        service.currentChangeIndex--;
        var change = service.pendingChanges[service.currentChangeIndex];
        service.navigateToField(change);
        $rootScope.$broadcast("dealTermsImport:changeNavigated", {
          index: service.currentChangeIndex,
          change: change
        });
      }
    };

    /**
     * Make the field with the given path the current active change.
     * Used when the user clicks or focuses a control directly.
     * @param {String} fieldPath - Path relative to `lease.` (e.g. 'tenantInfo.name')
     */
    service.setCurrentChangeByFieldPath = function(fieldPath) {
      if (!fieldPath) return;

      var idx = service.pendingChanges.findIndex(function(c) {
        return c && c.path === fieldPath;
      });

      if (idx === -1) {
        return;
      }

      service.currentChangeIndex = idx;
      var change = service.pendingChanges[idx];

      // Optionally scroll to the field
      service.navigateToField(change);

      $rootScope.$broadcast("dealTermsImport:changeNavigated", {
        index: service.currentChangeIndex,
        change: change
      });
    };

    /**
     * Check if a field has a pending change
     * @param {String} fieldPath - The path to the field
     * @returns {Object|null} The change object or null
     */
    service.getChangeForField = function(fieldPath) {
      return (
        service.pendingChanges.find(function(change) {
          return change.path === fieldPath;
        }) || null
      );
    };

    // Mirror the editor's own field lookup (`cleanLeaseVarName`,
    // lease_editor_controller.js:15200) by dropping every bracket segment. A
    // repeated control ships its `for` with a literal `[$index]` — 150 of the
    // ~1,180 shipped controls, e.g. `lease.rentPeriods[$index].rentPerSqFoot`
    // — while a routed conflict carries whatever the document rendered, e.g.
    // `rentPeriods[0].rentPerSqFoot`. Comparing the two verbatim would report
    // "no control" for every repeated deal term and strand conflicts that
    // accept and reject correctly today.
    function normalizeControlPath(path) {
      if (!path) return "";
      var fullPath = path.startsWith("lease.") ? path : "lease." + path;
      return fullPath.replace(/\[[^\]]*\]/g, "");
    }

    function editorControlForFieldPath(fieldPath) {
      if (!fieldPath || !document.querySelectorAll) return null;

      var wanted = normalizeControlPath(fieldPath);
      var controls = document.querySelectorAll("editor-control[for]");

      for (var i = 0; i < controls.length; i++) {
        if (normalizeControlPath(controls[i].getAttribute("for")) === wanted) {
          return controls[i];
        }
      }

      return null;
    }

    // setValueByPath only understands a numeric `[0]` segment. A path whose
    // index never interpolated — the `ng-attr-name` fallback in atomFieldPath
    // keeping `[$index]` or `[{{$index}}]` — would be walked as a literal
    // object key and fabricate `lease["rentPeriods[$index]"]` on the model.
    function fieldPathIsWritable(fieldPath) {
      if (!fieldPath) return false;

      var brackets = String(fieldPath).match(/\[[^\]]*\]/g) || [];
      for (var i = 0; i < brackets.length; i++) {
        if (!/^\[\d+\]$/.test(brackets[i])) return false;
      }
      return true;
    }

    var ADVISORY_MESSAGES = {
      derived_array:
        "The proposed generated schedule differs from the source. Review the schedule's underlying deal terms; this derived table cannot be replaced directly.",
      unresolved_path:
        "The proposed value could not be tied to a specific deal term in this form and cannot be applied automatically.",
      no_control:
        "The proposed value has no editable deal-term control in this form and cannot be applied automatically."
    };

    function routedConflictRefusal(change, lease) {
      if (!change || !change.routedFieldConflict) return null;

      if (Array.isArray(getValueAtSnapshotPath(lease, change.path))) {
        return "derived_array";
      }
      if (!fieldPathIsWritable(change.path)) {
        return "unresolved_path";
      }
      if (!editorControlForFieldPath(change.path)) {
        return "no_control";
      }
      return null;
    }

    service.queueFieldConflicts = function(changes) {
      if (!Array.isArray(changes) || changes.length === 0) return;

      var appScope = $rootScope.findAppScope && $rootScope.findAppScope();
      var lease = (appScope && appScope.lease) || $rootScope.lease;

      changes.forEach(function(change) {
        if (!change || !change.path) return;

        // A routed conflict carries the projection's rendered label as
        // `before`. Prefer the lease's own value where the path resolves, so
        // rejecting a dropdown or boolean restores domain data rather than
        // display text — but never overwrite `before` with `undefined`. A
        // lease-var whose name never interpolated (the `ng-attr-name` fallback
        // in atomFieldPath still holding a `{{$index}}` segment) resolves to
        // nothing, and rejectChangeForField would then write `undefined` over
        // the field instead of restoring it.
        var leaseValue = getValueAtSnapshotPath(lease, change.path);
        var restoreValue = leaseValue === undefined ? change.before : leaseValue;
        var routedChange = Object.assign({}, change, {
          routedFieldConflict: true
        });
        var refusal = routedConflictRefusal(routedChange, lease);
        var canApply = !refusal;
        var existing = service.getChangeForField(change.path);
        var pendingChange = Object.assign({}, routedChange, {
          before: restoreValue,
          current: restoreValue,
          decision: null,
          applied: false,
          advisory: !canApply
        });

        service.advisoryChanges = service.advisoryChanges.filter(function(
          advisory
        ) {
          return advisory.path !== change.path;
        });

        if (!canApply) {
          if (existing) {
            service.pendingChanges.splice(
              service.pendingChanges.indexOf(existing),
              1
            );
          }
          pendingChange.advisoryReason = refusal;
          pendingChange.message = ADVISORY_MESSAGES[refusal];
          service.advisoryChanges.push(pendingChange);
          return;
        }

        if (existing) {
          service.pendingChanges[
            service.pendingChanges.indexOf(existing)
          ] = pendingChange;
        } else {
          service.pendingChanges.push(pendingChange);
        }
      });

      if (
        service.pendingChanges.length === 0 &&
        service.advisoryChanges.length === 0
      )
        return;

      service.isImportActive = true;
      service.currentChangeIndex = service.pendingChanges.length > 0 ? 0 : -1;
      if (document.body) {
        document.body.classList.add("deal-terms-import-expanded");
      }
      $rootScope.$broadcast("dealTermsImport:started", {
        changes: service.pendingChanges,
        advisories: service.advisoryChanges
      });
    };

    function rerenderAcceptedField() {
      service.triggerChangeEvents();
      service.updateCalculatedFields();
    }

    /**
     * Reject a pending change for a specific field and revert its value.
     * The change remains in the pending list but is marked as rejected so
     * the user can toggle it back to accepted later.
     * @param {String} fieldPath - The path to the field (without 'lease.' prefix)
     */
    service.rejectChangeForField = function(fieldPath) {
      if (!fieldPath) return;

      var change = service.getChangeForField(fieldPath);
      if (!change) return;

      try {
        // Revert the lease value back to the original `before` value
        var appScope = $rootScope.findAppScope && $rootScope.findAppScope();
        var lease = (appScope && appScope.lease) || $rootScope.lease;

        var revertRefusal = routedConflictRefusal(change, lease);
        if (revertRefusal) {
          console.warn(
            "Refusing to revert a routed field conflict (" +
              revertRefusal +
              "):",
            fieldPath
          );
          return;
        }

        if (lease) {
          setValueByPath(lease, change.path, change.before);
        }

        change.decision = "rejected";
        change.applied = false;
      } catch (e) {
        console.warn(
          "Failed to revert deal terms change for field:",
          fieldPath,
          e
        );
      }

      // Persist and recalculate after reverting
      try {
        service.saveChangesToLease();
      } catch (e) {
        console.warn(
          "Failed to save lease after rejecting deal terms change:",
          e
        );
      }

      // Notify listeners that a change was rejected/updated
      $rootScope.$broadcast("dealTermsImport:changeRejected", {
        path: fieldPath,
        change: change
      });
    };

    /**
     * Accept a pending change for a specific field (apply the 'after' value).
     * The change remains in the pending list but is marked as accepted so the
     * user can still change their mind.
     * @param {String} fieldPath - The path to the field (without 'lease.' prefix)
     */
    service.acceptChangeForField = function(fieldPath) {
      if (!fieldPath) return;

      var change = service.getChangeForField(fieldPath);
      if (!change) return;

      try {
        var appScope = $rootScope.findAppScope && $rootScope.findAppScope();
        var lease = (appScope && appScope.lease) || $rootScope.lease;

        var applyRefusal = routedConflictRefusal(change, lease);
        if (applyRefusal) {
          console.warn(
            "Refusing to apply a routed field conflict (" + applyRefusal + "):",
            fieldPath
          );
          return;
        }

        if (lease) {
          setValueByPath(lease, change.path, change.after);

          // rerenderAcceptedField below runs the field's own ng-change, which
          // covers the rent and renewal chains. It cannot cover the section flags:
          // no ng-change derives them, and the controls that would are rendered
          // only when the flag they set is already on. Without this an imported
          // value sits on the lease with its section hidden, and therefore absent
          // from the download.
          if (appScope && appScope.normalizeDependentFlags) {
            appScope.normalizeDependentFlags(lease, change.path);
          }
        }

        change.decision = "accepted";
        change.applied = true;
        rerenderAcceptedField();
      } catch (e) {
        console.warn(
          "Failed to apply deal terms change for field:",
          fieldPath,
          e
        );
      }

      try {
        service.saveChangesToLease();
      } catch (e) {
        console.warn(
          "Failed to save lease after accepting deal terms change:",
          e
        );
      }

      $rootScope.$broadcast("dealTermsImport:changeAccepted", {
        path: fieldPath,
        change: change
      });
    };

    /**
     * Convert an array of changes with path strings to a nested object structure
     * @param {Array} changes - Array of change objects with path and before values
     * @returns {Object} Nested object structure matching the lease structure
     */
    function buildObjectFromChanges(changes) {
      var result = {};

      changes.forEach(function(change) {
        if (!change || !change.path) return;

        // Split path by dots, but preserve array notation
        // e.g., "rentPeriods[0].totalRent" -> ["rentPeriods[0]", "totalRent"]
        var pathParts = change.path.split(".");
        var current = result;

        // Navigate/create nested structure for all parts except the last
        for (var i = 0; i < pathParts.length - 1; i++) {
          var part = pathParts[i];

          // Handle array notation (e.g., "rentPeriods[0]")
          var arrayMatch = part.match(/^(.+?)\[(\d+)\]$/);
          if (arrayMatch) {
            var arrayName = arrayMatch[1];
            var arrayIndex = parseInt(arrayMatch[2], 10);

            if (!current[arrayName]) {
              current[arrayName] = [];
            }
            // Ensure array is large enough
            while (current[arrayName].length <= arrayIndex) {
              current[arrayName].push({});
            }
            if (
              !current[arrayName][arrayIndex] ||
              typeof current[arrayName][arrayIndex] !== "object"
            ) {
              current[arrayName][arrayIndex] = {};
            }
            current = current[arrayName][arrayIndex];
          } else {
            if (
              !current[part] ||
              typeof current[part] !== "object" ||
              Array.isArray(current[part])
            ) {
              current[part] = {};
            }
            current = current[part];
          }
        }

        // Set the final value
        var finalPart = pathParts[pathParts.length - 1];
        var finalArrayMatch = finalPart.match(/^(.+?)\[(\d+)\]$/);

        if (finalArrayMatch) {
          // Final part is an array element itself (e.g., "items[0]")
          var finalArrayName = finalArrayMatch[1];
          var finalArrayIndex = parseInt(finalArrayMatch[2], 10);

          if (!current[finalArrayName]) {
            current[finalArrayName] = [];
          }
          while (current[finalArrayName].length <= finalArrayIndex) {
            current[finalArrayName].push(null);
          }
          current[finalArrayName][finalArrayIndex] = change.before;
        } else {
          // Final part is a property (e.g., "totalRent" in "rentPeriods[0].totalRent")
          current[finalPart] = change.before;
        }
      });

      return result;
    }

    /**
     * Reject all pending changes and revert all values to their original state.
     * This uses the same processing logic as initializeImport but with the original snapshot.
     */
    service.rejectAllChanges = function() {
      if (!service.pendingChanges || service.pendingChanges.length === 0) {
        return;
      }

      // Get the current lease object
      var appScope = $rootScope.findAppScope && $rootScope.findAppScope();
      var currentLease = (appScope && appScope.lease) || $rootScope.lease;

      if (!currentLease) {
        console.warn("Cannot reject changes: lease object not found");
        return;
      }

      // Check if we have the original lease snapshot
      if (!service.originalLeaseSnapshot) {
        console.warn(
          "Cannot reject changes: original lease snapshot not found. Falling back to pendingChanges.before values."
        );
        // Fallback: use the buildObjectFromChanges approach if snapshot is missing
        var originalValues = buildObjectFromChanges(service.pendingChanges);

        // Capture current values before restoring
        var beforeValues = captureCurrentValues();

        // Use the shared function to apply values and process changes
        return new Promise(function(resolve, reject) {
          try {
            service.isProcessing = true;
            scrollToTop();
            $rootScope.$broadcast("dealTermsImport:processingStarted");

            applyValuesAndProcessChanges(
              currentLease,
              originalValues,
              beforeValues,
              function() {
                // After processing, finish the import
                trackImportOutcome("reject_all");
                service.finishImport({ skipOutcomeTracking: true });
                $rootScope.$broadcast("dealTermsImport:allChangesRejected");
                resolve();
              },
              reject
            );
          } catch (error) {
            service.isProcessing = false;
            $rootScope.$broadcast("dealTermsImport:processingFinished");
            reject(error);
          }
        });
      } else {
        // Capture current values before restoring (these will be the "before" values)
        var beforeValues = captureCurrentValues();
        var snapshot = service.originalLeaseSnapshot;

        // Use the shared function to apply snapshot and process changes
        // Pass true to completely replace the lease
        return new Promise(function(resolve, reject) {
          try {
            service.isProcessing = true;
            scrollToTop();
            $rootScope.$broadcast("dealTermsImport:processingStarted");

            applyValuesAndProcessChanges(
              currentLease,
              snapshot,
              beforeValues,
              function() {
                // After processing, finish the import
                trackImportOutcome("reject_all");
                service.finishImport({ skipOutcomeTracking: true });
                $rootScope.$broadcast("dealTermsImport:allChangesRejected");
                resolve();
              },
              reject,
              true
            );
          } catch (error) {
            service.isProcessing = false;
            $rootScope.$broadcast("dealTermsImport:processingFinished");
            reject(error);
          }
        });
      }
    };

    /**
     * Finish the import process
     */
    service.finishImport = function(options) {
      options = options || {};
      if (!options.skipOutcomeTracking) {
        trackImportOutcome("completed");
      }

      service.isImportActive = false;
      service.pendingChanges = [];
      service.advisoryChanges = [];
      service.currentChangeIndex = -1;
      service.extractedFields = null;
      service.extractedNotes = [];
      service.extractedNotesSummary = "";
      service.fieldConstraints = [];
      service.rejectedFields = [];
      service.droppedNoteCount = 0;
      service.observedValues = {};
      service.originalLeaseSnapshot = null;

      // Remove class to restore sidebar to original size
      if (document.body) {
        document.body.classList.remove("deal-terms-import-expanded");
      }

      // Disconnect mutation observer if it exists
      if (service.mutationObserver) {
        service.mutationObserver.disconnect();
        service.mutationObserver = null;
      }

      // Broadcast that import has finished
      $rootScope.$broadcast("dealTermsImport:finished");
    };

    /**
     * Cancel the import process
     */
    service.cancelImport = function() {
      service.finishImport({ skipOutcomeTracking: true });
    };

    /**
     * Remove update() and updateCurrent(...) calls from ng-change expression
     * @param {String} expression - The ng-change expression string
     * @returns {String} The cleaned expression without update() and updateCurrent() calls
     */
    function cleanNgChangeExpression(expression) {
      if (!expression) return expression;

      // Remove update(); calls (with optional whitespace), INCLUDING any receiver
      // prefix. The templates write `scopeVar.update()`, not a bare `update()`,
      // and stripping only the call left the dangling `scopeVar.` behind — so
      // `scopeVar.update(); scopeVar.updateCurrent("lease.permittedUses");`
      // cleaned to `scopeVar. scopeVar.` and $eval threw
      // [$parse:ueoe] Unexpected end of expression. That killed the whole
      // expression, so any OTHER handler in the same ng-change never ran either.
      // Matches: update(); scopeVar.update() ; a.b.update( ); etc.
      expression = expression.replace(
        /(?:[\w$]+\s*\.\s*)*\bupdate\s*\(\s*\)\s*;?/g,
        "",
      );

      // Remove updateCurrent(...); calls (with any arguments)
      // Matches: updateCurrent("lease.field"); or updateCurrent('lease.field'); or updateCurrent(lease.field); etc.
      // This regex handles:
      // - Quoted strings (single or double quotes, with escaped quotes)
      // - Unquoted identifiers
      // - Optional semicolon
      // - Optional whitespace
      expression = expression.replace(
        /(?:[\w$]+\s*\.\s*)*\bupdateCurrent\s*\([^)]*\)\s*;?/g,
        "",
      );

      // Clean up multiple consecutive semicolons and whitespace
      expression = expression.replace(/;\s*;/g, ";");
      expression = expression.replace(/\s+/g, " ");
      expression = expression.trim();

      // Remove trailing semicolon if present
      expression = expression.replace(/;\s*$/, "");

      return expression;
    }

    /**
     * Execute ng-change handler for a given element
     * @param {HTMLElement} element - The input/select/textarea/checkbox/radio element
     */
    function executeNgChange(element) {
      if (!element) return;

      try {
        // Get the Angular scope for this element
        var $element = angular.element(element);
        var scope = $element.scope();

        if (!scope) {
          // Try to get scope from parent
          scope = $element.parent().scope();
        }

        if (!scope) {
          // Try to get scope from editor-control parent
          var controlElement = element.closest("editor-control");
          if (controlElement) {
            scope = angular.element(controlElement).scope();
          }
        }

        if (!scope) {
          // Fallback to $rootScope
          scope = $rootScope;
        }

        // Find ng-change attribute
        var ngChangeAttr = element.getAttribute("ng-change");
        if (!ngChangeAttr) {
          // Check parent elements (for md-checkbox, md-radio-group)
          var parent = element.parentElement;
          while (parent && !ngChangeAttr) {
            ngChangeAttr = parent.getAttribute("ng-change");
            if (!ngChangeAttr && parent.tagName === "EDITOR-CONTROL") {
              break; // Don't go beyond editor-control
            }
            parent = parent.parentElement;
          }
        }

        if (ngChangeAttr) {
          // Clean the expression to remove update() and updateCurrent() calls
          var cleanedExpression = cleanNgChangeExpression(ngChangeAttr);

          // Only execute if there's something left after cleaning
          if (cleanedExpression && cleanedExpression.trim()) {
            // Execute the cleaned ng-change expression in the scope
            scope.$eval(cleanedExpression);
          }
        }
      } catch (error) {
        console.warn("Error executing ng-change for element:", element, error);
      }
    }

    /**
     * Setup MutationObserver to watch for value changes and execute ng-change handlers
     */
    function setupChangeObserver() {
      // Disconnect existing observer if any
      if (service.mutationObserver) {
        service.mutationObserver.disconnect();
      }

      // Create observer to watch for value/checked changes
      service.mutationObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === "attributes") {
            var target = mutation.target;

            // Watch for value or checked attribute changes
            if (
              mutation.attributeName === "value" ||
              mutation.attributeName === "checked" ||
              mutation.attributeName === "ng-reflect-model"
            ) {
              // Find the actual input element
              var input = target;
              if (
                target.tagName === "MD-CHECKBOX" ||
                target.tagName === "MD-RADIO-GROUP"
              ) {
                // For Angular Material components, the actual input might be nested
                input = target;
              } else if (
                target.tagName !== "INPUT" &&
                target.tagName !== "SELECT" &&
                target.tagName !== "TEXTAREA"
              ) {
                // Find input within the element
                input = target.querySelector("input, select, textarea");
              }

              if (input) {
                // Execute ng-change handler
                executeNgChange(input);
              }
            }
          } else if (mutation.type === "childList") {
            // Watch for new nodes being added (for dynamic content)
            mutation.addedNodes.forEach(function(node) {
              if (node.nodeType === 1) {
                // Element node
                var inputs =
                  node.querySelectorAll &&
                  node.querySelectorAll(
                    "input, select, textarea, md-checkbox, md-radio-group"
                  );
                if (inputs && inputs.length > 0) {
                  // New inputs added, trigger ng-change for each input
                  inputs.forEach(function(input) {
                    executeNgChange(input);
                  });
                }
              }
            });
          }
        });
      });

      // Observe all editor-control elements for attribute changes
      var observerTarget =
        document.querySelector(".editor-cards-container") || document.body;
      service.mutationObserver.observe(observerTarget, {
        attributes: true,
        attributeFilter: ["value", "checked", "ng-reflect-model"],
        subtree: true,
        childList: true
      });
    }

    /**
     * Trigger change handlers for all controls that were updated during import
     * Uses MutationObserver to detect changes and execute ng-change handlers
     */
    service.triggerChangeEvents = function() {
      if (!service.pendingChanges || service.pendingChanges.length === 0) {
        return;
      }

      // Setup observer to watch for changes
      setupChangeObserver();

      // Use a timeout to ensure DOM is ready after Angular digest
      setTimeout(function() {
        service.pendingChanges.forEach(function(change) {
          if (!change.path) return;

          try {
            // Build the full path for the 'for' attribute (e.g., 'lease.tenantInfo.name')
            var fullPath = change.path.startsWith("lease.")
              ? change.path
              : "lease." + change.path;

            // Check if this is an array field
            var arrayMatch = change.path.match(/^(.+?)\[(\d+)\](?:\.(.+))?$/);

            var controlElement = null;
            var input = null;

            if (arrayMatch) {
              // Array field - find all matching elements with the correct index
              var basePath = arrayMatch[1];
              var index = parseInt(arrayMatch[2], 10);
              var fieldName = arrayMatch[3];
              var fullBasePath = basePath.startsWith("lease.")
                ? basePath
                : "lease." + basePath;

              // Query for editor-control elements with matching base path and index
              var selector =
                'editor-control[for*="' +
                fullBasePath +
                '"][index="' +
                index +
                '"]';
              var elements = document.querySelectorAll(selector);

              // Filter to find the one with the field name in the 'for' attribute
              for (var i = 0; i < elements.length; i++) {
                var forAttr = elements[i].getAttribute("for") || "";
                if (fieldName && forAttr.indexOf(fieldName) === -1) {
                  continue;
                }
                controlElement = elements[i];
                break;
              }
            } else {
              // Regular field - find by exact 'for' attribute match
              controlElement = document.querySelector(
                'editor-control[for="' + fullPath + '"]'
              );

              if (!controlElement) {
                // Try without 'lease.' prefix
                controlElement = document.querySelector(
                  'editor-control[for="' + change.path + '"]'
                );
              }
            }

            if (controlElement) {
              // Find input, select, textarea, checkbox, or radio within this editor-control
              input = controlElement.querySelector("input, select, textarea");
              var mdCheckbox = controlElement.querySelector("md-checkbox");
              var mdRadioGroup = controlElement.querySelector("md-radio-group");

              if (input) {
                // Execute ng-change handler directly
                executeNgChange(input);
              } else if (mdCheckbox) {
                executeNgChange(mdCheckbox);
              } else if (mdRadioGroup) {
                executeNgChange(mdRadioGroup);
              }
            }
          } catch (error) {
            console.warn(
              "Error triggering change event for path:",
              change.path,
              error
            );
          }
        });

        // Disconnect observer after processing (we'll reconnect if needed)
        setTimeout(function() {
          if (service.mutationObserver) {
            service.mutationObserver.disconnect();
            service.mutationObserver = null;
          }
        }, 500); // Give some time for any delayed updates
      }, 100);
    };

    return service;
  }
]);
